import { Button } from "@/components/ui/button";
import {
  PLATFORM_ENTITY_SELECTORS,
  getPlatformEntitySelector,
} from "@/constants/integration-platforms";
import { cn } from "@/utils";
import { IntegrationConnection } from "@cohesive/api-client";
import { Entity, IntegrationIcon } from "@cohesive/ui-components";
import { ErrorBoundary } from "@sentry/react";
import { ChevronLeft } from "lucide-react";
import React from "react";
import { NotImplementedSelector } from "./integrations/NotImplementedSelector";

interface EntitySelectorProps {
  connection: IntegrationConnection;
  onClose: () => void;
  onSelect: (entity: Entity) => void;
  onBack?: () => void;
  backText?: string;
  className?: string;
  style?: React.CSSProperties;
}

// Export the centralized mapping for backward compatibility
export const INTEGRATION_COMPONENTS = PLATFORM_ENTITY_SELECTORS;

export const EntitySelector: React.FC<EntitySelectorProps> = ({
  connection,
  onSelect,
  onBack,
  backText = "Connectors",
  className,
  style,
}) => {
  const platform = connection?.integration_platform || "skills";

  let Component = getPlatformEntitySelector(platform) || NotImplementedSelector;

  return (
    <div
      className={cn(
        "bg-background/80 backdrop-blur-md overflow-hidden w-full h-full flex flex-col",
        className,
      )}
      style={{
        ...style,
        animation: "fadeIn 200ms ease-out forwards",
        animationDelay: "150ms",
        opacity: 0,
      }}
    >
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
      {/* Header with close button */}
      <div className="flex items-center justify-between px-4 py-2 bg-muted/50 border-b">
        <div className="flex items-center gap-2">
          {onBack && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={onBack}
                className="h-8 px-2 hover:bg-accent flex items-center gap-1"
              >
                <ChevronLeft className="h-4 w-4" />
                <span className="text-sm text-muted-foreground">
                  {backText}
                </span>
              </Button>
              <span className="text-muted-foreground">/</span>
            </>
          )}
          <div className="flex items-center gap-2">
            <IntegrationIcon
              platform={connection.integration_platform}
              name={connection.integration_name}
              imageURL={connection.integration_image_url}
              className="h-6 w-6 rounded object-cover"
            />
            <h3 className="font-medium text-sm">
              {connection.integration_name}
            </h3>
          </div>
        </div>
      </div>

      {/* Content Router */}
      <div className="flex-1 min-h-0">
        <div className="flex flex-col h-full">
          <ErrorBoundary
            fallback={
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <p>Error loading connection</p>
              </div>
            }
          >
            <Component
              connection={connection || ({} as IntegrationConnection)}
              onSelect={onSelect}
              className="border-0 h-full"
            />
          </ErrorBoundary>
        </div>
      </div>
    </div>
  );
};
